import { useEffect, useMemo, useRef, useState } from "react";
import { toJpeg } from "html-to-image";
import ImageButton from "components/molecules/ImageButton";
import { colors } from "constants/colors";
import {
  StyledText,
  StyledText10,
  StyledText11,
  StyledText9,
} from "styles/Text";
import { useAuthStore } from "stores/authStore";
import { useDeviceStore } from "stores/deviceStore";
import { toast } from "util/common";
import RoundRectangleButton from "components/atoms/RoundRectangleButton";
import { API_IMAGE_CONTEXT, ENV, MW_DOMAIN, ROOT_DOMAIN } from "constants/api";
import { PregnancyAnnounceReportModel } from "models/pregnancyAnnounceReportModel";
import RevealOvuGraph from "components/organisms/pregnancy/RevealOvuGraph";
import { PregnantImageModel } from "models/pregnantImageModel";
import { strToFormat } from "util/date";
import {
  DEFAULT_STEPS,
  INTRO_ANIMATION_POSITION,
  STEP_DURATION_MS,
  FADE_DURATION_S,
} from "constants/pregnancyRevealReport";
import * as RevealReportStyles from "./RevealReportContent.styles";
import { CATEGORY_LIST, CHAPTER_5_CONTENTS } from "data/pregnancyRevealReport";
import RevealStepCard from "./RevealStepCard";
import RevealCaptureCard from "./RevealCaptureCard";
import RevealCard from "./RevealCard";
import RevealIntroTextAnimation from "./RevealIntroTextAnimation";
import RevealMusicToggle from "./RevealMusicToggle";

// 임밍아웃 리포트
const RevealReportContent = ({
  data,
}: {
  data?: PregnancyAnnounceReportModel;
}) => {
  const DEFAULT_SHARE_MAX_STEP = 5; // 공유기준 최대 스텝
  const MAX_PREG_CHECKER = 5;
  const PLV_POSITIVITY = "PR01" as const;
  const PREGNANCY_REVEAL_TYPE = "pregnancyReveal" as const;
  const PREGNANCY_REVEAL_PURPOSE = {
    SHARE: "share",
    SAVE: "save",
    REGISTER: "register",
  } as const;

  const token = useAuthStore((state) => state.token);
  const safeAreaTop = useDeviceStore((state) => state.safeAreaTop);
  const safeAreaBottom = useDeviceStore((state) => state.safeAreaBottom);

  const [steps, setSteps] = useState(0);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revokeUrlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareCardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  const isMobileApp =
    typeof window !== "undefined" && !!window.ReactNativeWebView;

  const ua = navigator.userAgent.toLowerCase();
  const isShowHeaderCancel = ua.includes("kakaotalk")
    ? true
    : isMobileApp
      ? true
      : window.history.length > 1;
  const isShared = !isMobileApp || !token; // 모바일 앱이 아니거나 토큰이 없으면 공유페이지

  const maxStep = isShared ? DEFAULT_SHARE_MAX_STEP : DEFAULT_STEPS.length - 1;
  const visibleStepCount = maxStep + 1;
  const currentStep = DEFAULT_STEPS[steps];
  const chapter5List = isShared
    ? CHAPTER_5_CONTENTS.slice(0, DEFAULT_SHARE_MAX_STEP - 1)
    : CHAPTER_5_CONTENTS;

  const pregCheckerList: (PregnantImageModel & { diaryDtStr?: string })[] =
    useMemo(() => {
      if (!data?.pregGraphList) return [];

      let hasPr01 = false;

      return (
        data.pregGraphList.slice(0, MAX_PREG_CHECKER).map((item) => {
          let userPlv = item.userPlv;
          if (hasPr01) {
            // PR01(양성) 이후는 PR03로 처리
            userPlv = "PR03";
          }

          if (item.userPlv === PLV_POSITIVITY) {
            hasPr01 = true;
          }

          return {
            ...item,
            userPlv,
            diaryDtStr: item.diaryDt
              ? strToFormat(item.diaryDt, "M월 d일")
              : undefined,
          };
        }) || []
      );
    }, [data?.pregGraphList]);

  const lastPregnantImgDt = data?.lastPregnantImgDt
    ? strToFormat(data?.lastPregnantImgDt, "yyyy-MM-dd")
    : "-";

  useEffect(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (steps >= maxStep) return;
    advanceTimerRef.current = setTimeout(() => {
      setSteps((prev) => Math.min(prev + 1, maxStep));
    }, STEP_DURATION_MS);
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [steps, maxStep]);

  // 저장용 objectURL revoke 타이머 클린업
  useEffect(() => {
    return () => {
      if (revokeUrlTimerRef.current) {
        clearTimeout(revokeUrlTimerRef.current);
        revokeUrlTimerRef.current = null;
      }
    };
  }, []);

  // 공유 (RN stub과 동일)
  const onPressShare = () => {
    if (!window.ReactNativeWebView || !data?.encUsn) return;

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: PREGNANCY_REVEAL_TYPE,
        purpose: PREGNANCY_REVEAL_PURPOSE.SHARE,
        link: `${MW_DOMAIN}/pregnancy/revealReport/share?encUsn=${encodeURIComponent(data.encUsn)}`,
      }),
    );
  };

  // 이미지 저장 (공유 카드를 화면 크기에 맞춰 캡처해 웹에서 다운로드)
  const onPressSaveImg = async () => {
    if (isSavingImage || !shareCardRef.current || !containerRef.current) return;

    const { width, height } = containerRef.current.getBoundingClientRect();
    setIsSavingImage(true);

    try {
      const waitForImages = (root: HTMLElement) =>
        // 이미지 로드 대기
        Promise.all(
          Array.from(root.querySelectorAll("img")).map((img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((resolve) => {
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                }),
          ),
        );

      await waitForImages(shareCardRef.current);

      if (document.fonts?.ready) {
        // 폰트 로드 대기
        await document.fonts.ready;
      }

      const options = {
        pixelRatio: 2,
        quality: 0.92,
        backgroundColor: "#ffebc7",
        width,
        height,
        style: { width: `${width}px`, height: `${height}px` },
      };

      const isWebKit = (() => {
        // Webkit base의 브라우저에서 이미지가 백색으로 변경되는 이슈 대응
        const ua = navigator.userAgent;
        return (
          (/AppleWebKit/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)) ||
          /iPhone|iPad|iPod/i.test(ua)
        );
      })();

      if (isWebKit) {
        for (let i = 0; i < 3; i += 1) {
          // 이미지 백색 캡쳐되는 현상 대응용 warm-up
          await toJpeg(shareCardRef.current, options);
        }
      }

      const image = await toJpeg(shareCardRef.current, options);

      if (!image) return;

      if (window.ReactNativeWebView) {
        // RN저장 로직
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: PREGNANCY_REVEAL_TYPE,
            purpose: PREGNANCY_REVEAL_PURPOSE.SAVE,
            image,
          }),
        );
      } else {
        if (ua.includes("kakaotalk")) {
          // 카카오톡
          const link = document.createElement("a");
          link.href = image; // blob으로 바꾸지 말 것
          link.download = "image.jpeg";
          link.click();
          return;
        }

        // 웹저장 로직
        const blob = await (await fetch(image)).blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "image.jpeg";
        link.click();

        if (revokeUrlTimerRef.current) {
          clearTimeout(revokeUrlTimerRef.current);
        }
        revokeUrlTimerRef.current = setTimeout(() => {
          URL.revokeObjectURL(url);
          revokeUrlTimerRef.current = null;
        }, 1000);

        toast("이미지가 저장되었습니다.");
      }
    } catch {
      toast("이미지 저장에 실패했습니다.");
    } finally {
      setIsSavingImage(false);
    }
  };

  const onPressNav = (direction: "left" | "right") => {
    if (direction === "left") {
      if (steps === 0) return;
      setSteps((prev) => prev - 1);
    } else {
      if (steps === maxStep) return;
      setSteps((prev) => prev + 1);
    }
  };

  const onPressStartDeliveryDiary = () => {
    if (!window.ReactNativeWebView) return;

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: PREGNANCY_REVEAL_TYPE,
        purpose: PREGNANCY_REVEAL_PURPOSE.REGISTER,
      }),
    );
  };

  const onPressNext = () => {
    window.open(
      ENV === "release"
        ? `https://${ROOT_DOMAIN}/web/dl/11m7li6izk1jy9s`
        : `https://dev.${ROOT_DOMAIN}/web/dl/6v9b5rlsqso3fcc`,
      "_blank",
    );
  };

  const onPressClose = () => {
    if (window.ReactNativeWebView) {
      // 웹뷰
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "close", link: "" }),
      );
      return;
    }

    if (ua.includes("kakaotalk")) {
      // 카카오톡
      window.close();
      return;
    }

    window.history.back();
  };

  if (!data) return null;

  return (
    <RevealReportStyles.StyledContainer
      ref={containerRef}
      $startColor={currentStep.startColor}
      $endColor={currentStep.endColor}
      $safeAreaBottom={safeAreaBottom}
    >
      <h1 className="hide">열달후에</h1>
      <RevealReportStyles.StyledInnerContainer>
        <RevealReportStyles.StyledCustomHeader $safeAreaTop={safeAreaTop}>
          <RevealReportStyles.StyledHeaderRow>
            {isShowHeaderCancel && (
              <RevealReportStyles.StyledHeaderBox>
                <RevealReportStyles.StyledHeaderCancelBtn
                  onClick={onPressClose}
                  type="button"
                >
                  <img
                    width={36}
                    height={36}
                    src={require("assets/images/ic_close_normal_outlined_white_m.png")}
                    alt="close icon"
                  />
                </RevealReportStyles.StyledHeaderCancelBtn>
              </RevealReportStyles.StyledHeaderBox>
            )}
            {!isShared && (
              <RevealReportStyles.StyledHeaderBox>
                <ImageButton
                  width={36}
                  height={36}
                  source={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/ic_shared_w.png`}
                  onClick={onPressShare}
                />
              </RevealReportStyles.StyledHeaderBox>
            )}
          </RevealReportStyles.StyledHeaderRow>

          <RevealReportStyles.StyledStepBar>
            {DEFAULT_STEPS.slice(0, visibleStepCount).map((_, index) => {
              const isCompleted = index < steps;
              const isCurrent = index === steps;

              return (
                <RevealReportStyles.StyledStepItem key={`${index}-step-item`}>
                  <RevealReportStyles.StyledStepItemBg />
                  {isCompleted && <RevealReportStyles.StyledStepItemFill />}
                  {isCurrent && (
                    <RevealReportStyles.StyledStepItemFillAnimating
                      key={`step-item-fill-${steps}`}
                    />
                  )}
                </RevealReportStyles.StyledStepItem>
              );
            })}
          </RevealReportStyles.StyledStepBar>
        </RevealReportStyles.StyledCustomHeader>

        <RevealReportStyles.StyledContentBox>
          <RevealReportStyles.StyledContentInnerBox>
            <RevealMusicToggle />
            {steps === 0 && (
              <RevealStepCard>
                <RevealReportStyles.StyledIntroAnimationBgBox>
                  {INTRO_ANIMATION_POSITION.map((item, index) => {
                    return (
                      <RevealReportStyles.StyledIntroAnimationBgItem
                        key={`intro-animation-bg-item-${index}`}
                        $index={index}
                        $size={item.size}
                        $left={item.left}
                        $top={item.top}
                        src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/img_deco_star.png`}
                      />
                    );
                  })}
                </RevealReportStyles.StyledIntroAnimationBgBox>

                <RevealReportStyles.StyledStepIntroBox>
                  <RevealReportStyles.StyledStepAnimationBox
                    $delay={FADE_DURATION_S}
                  >
                    <RevealReportStyles.StyledMainImageBox>
                      <img
                        src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/ic_heart_selected_filled_orange_m.png`}
                        width={48}
                        height={48}
                        alt="heart icon"
                      />
                    </RevealReportStyles.StyledMainImageBox>
                  </RevealReportStyles.StyledStepAnimationBox>
                  <RevealIntroTextAnimation
                    dateTitle={data.lastPregnantImgDtTitle ?? "-"}
                    mainTitle={"엄마 ∙ 아빠가\n되었어요"}
                    bodyText={
                      "임신을 준비하며 기록한\n모든 순간들을 모아봤어요."
                    }
                    startDelayS={FADE_DURATION_S}
                  />
                </RevealReportStyles.StyledStepIntroBox>
              </RevealStepCard>
            )}

            {steps === 1 && (
              <RevealStepCard
                title={`아기 천사를 만나기까지\n${data.pregnantMensesDiffDays ?? "-"}일간의 여정`}
              >
                <RevealReportStyles.StyledStepAnimationBox>
                  <RevealReportStyles.StyledCardWithBorder>
                    <RevealReportStyles.StyledJourneyContentLeft>
                      <RevealReportStyles.StyledIconWrapper>
                        <img
                          src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/ic_quick_checker_pregnancy_solid.png`}
                          width={24}
                          height={24}
                          alt="checker icon image"
                        />
                      </RevealReportStyles.StyledIconWrapper>
                      <RevealReportStyles.StyledJourneyContentTextBox>
                        <StyledText.Sub12 color={colors.white}>
                          처음 두 줄을 확인 한 날
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.diaryDtStr ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledJourneyContentTextBox>
                    </RevealReportStyles.StyledJourneyContentLeft>
                    <StyledText11 color={"#FFCF33"} fontWeight="500">
                      {data.pregnantTryMonths ?? "-"}개월만에 만났어요!
                    </StyledText11>
                  </RevealReportStyles.StyledCardWithBorder>
                </RevealReportStyles.StyledStepAnimationBox>

                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 2}
                >
                  <RevealReportStyles.StyledJourneyLineBox>
                    <StyledText.Sub12 color={colors.white}>
                      {strToFormat(data.firstOvuDt ?? "", "MM월 dd일 ")}
                      배란일부터 두줄까지
                    </StyledText.Sub12>
                    <StyledText.Headline15 color={colors.white}>
                      {data.pregnantOvuDiffDays ?? "-"}일간의 기다림
                    </StyledText.Headline15>
                  </RevealReportStyles.StyledJourneyLineBox>

                  <RevealReportStyles.StyledJourneyLineBox>
                    <StyledText.Sub12 color={colors.white}>
                      배란 테스트 기록
                    </StyledText.Sub12>
                    <StyledText.Headline15 color={colors.white}>
                      {data.ovuTesterCnt ?? "-"}회
                    </StyledText.Headline15>
                  </RevealReportStyles.StyledJourneyLineBox>

                  <RevealReportStyles.StyledJourneyLineBox $isLast>
                    <StyledText.Sub12 color={colors.white}>
                      기록한 생리주기
                    </StyledText.Sub12>
                    <StyledText.Headline15 color={colors.white}>
                      {data.mensesHistoryCnt ?? "-"}회
                    </StyledText.Headline15>
                  </RevealReportStyles.StyledJourneyLineBox>
                </RevealReportStyles.StyledStepAnimationBox>

                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 3}
                >
                  <RevealReportStyles.StyledCardWithBorder>
                    <RevealReportStyles.StyledJourneyContentLeft>
                      <RevealReportStyles.StyledIconWrapper>
                        <img
                          src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/ic_quick_benefit_solid.png`}
                          width={24}
                          height={24}
                          alt="benefit icon image"
                        />
                      </RevealReportStyles.StyledIconWrapper>
                      <RevealReportStyles.StyledJourneyContentTextBox>
                        <StyledText.Sub12 color={colors.white}>
                          임신 준비를 시작 한 날
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.firstMensesStartDtStr ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledJourneyContentTextBox>
                    </RevealReportStyles.StyledJourneyContentLeft>
                  </RevealReportStyles.StyledCardWithBorder>
                </RevealReportStyles.StyledStepAnimationBox>
              </RevealStepCard>
            )}

            {steps === 2 && (
              <RevealStepCard title={`아기는\n이렇게 찾아왔어요.`}>
                <RevealReportStyles.StyledStepAnimationBox $marginBottom={12}>
                  <RevealReportStyles.StyledCard>
                    <RevealReportStyles.StyledCardTitleInner>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Sub12 color={colors.white}>
                          예상 임신일 (예상 착상 시기)
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.ovuDtBeforePregImgStr ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledCardTitleBox>

                      {data.ovuGraphList && data.ovuGraphList.length > 0 && (
                        <RevealReportStyles.StyledGraphBox>
                          <StyledText.Sub12 color={colors.white}>
                            최근 배란 주기 흐름
                          </StyledText.Sub12>
                          <RevealOvuGraph graphData={data.ovuGraphList} />
                        </RevealReportStyles.StyledGraphBox>
                      )}
                    </RevealReportStyles.StyledCardTitleInner>
                  </RevealReportStyles.StyledCard>
                </RevealReportStyles.StyledStepAnimationBox>

                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 2}
                >
                  <RevealReportStyles.StyledCard>
                    <RevealReportStyles.StyledCardBottomInner>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Sub12 color={colors.white}>
                          처음 두 줄을 확인한 날
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.diaryDt
                            ? strToFormat(data.diaryDt, "M월 d일")
                            : "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledCardTitleBox>
                      {pregCheckerList && pregCheckerList.length > 0 && (
                        <RevealReportStyles.StyledTimelineBox>
                          {pregCheckerList?.map((item, index) => (
                            <RevealReportStyles.StyledTimelineItemBox
                              $opacity={
                                ((pregCheckerList?.length ?? 0) - index) /
                                (pregCheckerList?.length ?? 0)
                              }
                              key={`${item.pregnantImageId}-${index}`}
                            >
                              <StyledText9 color={colors.white}>
                                {item.diaryDtStr}
                                {item.userPlv === PLV_POSITIVITY && "🎉"}
                              </StyledText9>
                              <img
                                src={`${API_IMAGE_CONTEXT}${item.pregfilePath ?? ""}`}
                                alt="ovulation checker image"
                                width={60}
                                height={15}
                              />
                            </RevealReportStyles.StyledTimelineItemBox>
                          ))}
                        </RevealReportStyles.StyledTimelineBox>
                      )}
                    </RevealReportStyles.StyledCardBottomInner>
                  </RevealReportStyles.StyledCard>
                </RevealReportStyles.StyledStepAnimationBox>
              </RevealStepCard>
            )}

            {steps === 3 && (
              <RevealStepCard title={`지금 우리 아기는\n이렇게 생겼어요.`}>
                <RevealReportStyles.StyledStepAnimationBox $marginBottom={12}>
                  <RevealReportStyles.StyledCard>
                    <RevealReportStyles.StyledFetusCardInner>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Headline17
                          color={colors.white}
                          $textAlign="center"
                        >
                          {data.estimatePreganantWeeksTitle ?? "-"}
                        </StyledText.Headline17>
                        <StyledText.Sub12
                          color={colors.white}
                          $textAlign="center"
                        >
                          {data.estimatePreganantWeeksSummary ?? "-"}
                        </StyledText.Sub12>
                      </RevealReportStyles.StyledCardTitleBox>
                      {data.estimatePreganantWeeksImg && (
                        <RevealReportStyles.StyledFetusImg
                          src={`${API_IMAGE_CONTEXT}${data.estimatePreganantWeeksImg ?? ""}`}
                          alt="fetus image"
                        />
                      )}
                    </RevealReportStyles.StyledFetusCardInner>
                  </RevealReportStyles.StyledCard>
                </RevealReportStyles.StyledStepAnimationBox>

                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 2}
                  $marginBottom={12}
                >
                  <RevealReportStyles.StyledCardWrapper>
                    <RevealReportStyles.StyledTextCard>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Sub12 color={colors.white}>
                          현재 임신 주수
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.estimatePreganantWeeks ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledCardTitleBox>
                    </RevealReportStyles.StyledTextCard>

                    <RevealReportStyles.StyledTextCard>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Sub12 color={colors.white}>
                          병원 방문 권장일
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.nextRecommendVisit ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledCardTitleBox>
                    </RevealReportStyles.StyledTextCard>
                  </RevealReportStyles.StyledCardWrapper>
                </RevealReportStyles.StyledStepAnimationBox>

                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 3}
                >
                  <RevealReportStyles.StyledCard>
                    <RevealReportStyles.StyledCardInner>
                      <RevealReportStyles.StyledCardTitleBox>
                        <StyledText.Sub12 color={colors.white}>
                          예상 출생일
                        </StyledText.Sub12>
                        <StyledText.Headline17 color={colors.white}>
                          {data.expectBirthDtStr ?? "-"}
                        </StyledText.Headline17>
                      </RevealReportStyles.StyledCardTitleBox>
                    </RevealReportStyles.StyledCardInner>
                  </RevealReportStyles.StyledCard>
                </RevealReportStyles.StyledStepAnimationBox>
              </RevealStepCard>
            )}

            {steps === 4 && (
              <RevealStepCard title={`소중한 사람들에게\n알려보세요.`}>
                <RevealReportStyles.StyledRevealBox>
                  <RevealReportStyles.StyledStepAnimationBox
                    $delay={FADE_DURATION_S}
                  >
                    <RevealCard
                      imageUrl={`${API_IMAGE_CONTEXT}${data.lastPregnantFilePath ?? ""}`}
                      date={lastPregnantImgDt}
                    >
                      <RevealReportStyles.StyledCardButton
                        type="button"
                        disabled={isSavingImage}
                        onClick={onPressSaveImg}
                      >
                        <img
                          width={24}
                          height={24}
                          src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/ic_download.png`}
                          alt="download icon"
                        />
                        <StyledText.Headline12>
                          이미지 저장
                        </StyledText.Headline12>
                      </RevealReportStyles.StyledCardButton>
                    </RevealCard>
                  </RevealReportStyles.StyledStepAnimationBox>
                </RevealReportStyles.StyledRevealBox>
              </RevealStepCard>
            )}

            {steps === 5 && (
              <RevealStepCard title={`우리 아기를\n계속해서 기록해보세요.`}>
                {chapter5List.map((item, index) => {
                  const { title, subtitle, src } = item;
                  return (
                    <RevealReportStyles.StyledStepAnimationBox
                      $delay={FADE_DURATION_S * (index + 1)}
                      $marginBottom={index === chapter5List.length - 1 ? 0 : 12}
                      key={`${index}-${title}`}
                    >
                      <RevealReportStyles.StyledCardWithBorder>
                        <RevealReportStyles.StyledJourneyContentLeft>
                          <RevealReportStyles.StyledIconWrapper>
                            <img
                              src={src}
                              width={24}
                              height={24}
                              alt={`${subtitle} icon`}
                            />
                          </RevealReportStyles.StyledIconWrapper>
                          <RevealReportStyles.StyledJourneyContentTextBox>
                            <StyledText.Sub12 color={colors.white}>
                              {title}
                            </StyledText.Sub12>
                            <StyledText.Headline17 color={colors.white}>
                              {subtitle}
                            </StyledText.Headline17>
                          </RevealReportStyles.StyledJourneyContentTextBox>
                        </RevealReportStyles.StyledJourneyContentLeft>
                      </RevealReportStyles.StyledCardWithBorder>
                    </RevealReportStyles.StyledStepAnimationBox>
                  );
                })}

                {isShared && (
                  <RevealReportStyles.StyledShareButtonBox>
                    <RevealReportStyles.StyledStepAnimationBox
                      $delay={FADE_DURATION_S * (chapter5List.length + 1)}
                    >
                      <RoundRectangleButton
                        text="앱에서 계속 공유받기"
                        onClick={onPressNext}
                        width={"100%"}
                        height={47}
                      />
                    </RevealReportStyles.StyledStepAnimationBox>
                  </RevealReportStyles.StyledShareButtonBox>
                )}
              </RevealStepCard>
            )}

            {steps === 6 && (
              <RevealStepCard title={`이제 엄마가 될 준비를\n시작해 볼까요?`}>
                <RevealReportStyles.StyledDiaryCardBox>
                  <RevealReportStyles.StyledStepAnimationBox
                    $delay={FADE_DURATION_S}
                  >
                    <RevealReportStyles.StyledDiaryCard
                      onClick={onPressStartDeliveryDiary}
                    >
                      <RevealReportStyles.StyledProfileBox>
                        <RevealReportStyles.StyledProfileBabyBox>
                          <RevealReportStyles.StyledProfileBabyImg
                            width={72}
                            height={72}
                            src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/img_babyprofile_peach.png`}
                            alt="baby profile image"
                          />
                          <RevealReportStyles.StyledProfileBabyTextBox>
                            <StyledText.Headline17>
                              우리아기
                            </StyledText.Headline17>
                            <StyledText10 color={colors.subBody3}>
                              예상 출산 예정일
                            </StyledText10>
                            <RevealReportStyles.StyledBabyText
                              color={colors.subBody3}
                            >
                              {data.expectBirthDtStr ?? "-"}
                            </RevealReportStyles.StyledBabyText>
                          </RevealReportStyles.StyledProfileBabyTextBox>
                        </RevealReportStyles.StyledProfileBabyBox>
                        <RevealReportStyles.StyledProfileCtBox>
                          {CATEGORY_LIST.map((item, index) => {
                            const { ctIcon, ctNm } = item;
                            return (
                              <RevealReportStyles.StyledProfileCtWrapper
                                key={`${index}-${ctNm}`}
                              >
                                <img width={24} height={24} src={ctIcon} />
                                <StyledText.Sub12>{ctNm}</StyledText.Sub12>
                              </RevealReportStyles.StyledProfileCtWrapper>
                            );
                          })}
                        </RevealReportStyles.StyledProfileCtBox>
                      </RevealReportStyles.StyledProfileBox>
                      <RoundRectangleButton
                        text="임신 모드 시작하기"
                        width={"100%"}
                      />
                    </RevealReportStyles.StyledDiaryCard>
                  </RevealReportStyles.StyledStepAnimationBox>
                </RevealReportStyles.StyledDiaryCardBox>
                <RevealReportStyles.StyledStepAnimationBox
                  $delay={FADE_DURATION_S * 2}
                >
                  <RevealReportStyles.StyledDiaryCardBottomBox>
                    <RevealReportStyles.StyledDiaryCardBottomTextBox
                      $opacity={0.6}
                    >
                      <StyledText.Body14 color={colors.white}>
                        다음 이야기는{" "}
                        <StyledText.Body14
                          fontWeight="500"
                          color={colors.white}
                        >
                          태아 성장 스토리
                        </StyledText.Body14>
                        에서 만나요!
                      </StyledText.Body14>
                    </RevealReportStyles.StyledDiaryCardBottomTextBox>
                    <RevealReportStyles.StyledDiaryCardBottomTextBox
                      $opacity={0.3}
                    >
                      <StyledText.Sub11
                        color={colors.white}
                        $textAlign="center"
                      >
                        지금처럼 기록을 이어가면,
                        <br />
                        우리 아기의 다음 이야기가 완성돼요.
                      </StyledText.Sub11>
                    </RevealReportStyles.StyledDiaryCardBottomTextBox>
                  </RevealReportStyles.StyledDiaryCardBottomBox>
                </RevealReportStyles.StyledStepAnimationBox>
              </RevealStepCard>
            )}
          </RevealReportStyles.StyledContentInnerBox>

          <RevealReportStyles.StyledNav>
            <RevealReportStyles.StyledNavHalf
              type="button"
              onClick={() => onPressNav("left")}
              aria-label="previous step"
            />
            <RevealReportStyles.StyledNavHalf
              type="button"
              onClick={() => onPressNav("right")}
              aria-label="next step"
            />
          </RevealReportStyles.StyledNav>
        </RevealReportStyles.StyledContentBox>

        <RevealReportStyles.StyledStepFooter>
          <img
            width={59}
            height={25}
            src={`${API_IMAGE_CONTEXT}/front/pregnancyRevealReport/img_logo_white.png`}
            alt="열달후에 logo"
          />
        </RevealReportStyles.StyledStepFooter>
      </RevealReportStyles.StyledInnerContainer>

      <RevealReportStyles.StyledCaptureLayer>
        <RevealReportStyles.StyledCaptureLayerCover />
        <RevealCaptureCard
          ref={shareCardRef}
          imageUrl={`${MW_DOMAIN}/images${data.lastPregnantFilePath ?? ""}`}
          date={lastPregnantImgDt}
        />
      </RevealReportStyles.StyledCaptureLayer>
    </RevealReportStyles.StyledContainer>
  );
};

export default RevealReportContent;
